import React, { useContext } from 'react';
import { PopperProps } from '@mui/material/Popper/BasePopper.types';
import { rafThrottle } from '../../utils/rafThrottle';
import { TooltipMode } from '../Chart.types';

export type ChartTooltipContextType<Datum> =
  | {
      updatePosition: (x: number, y: number) => void;
      hideTooltip: () => void;
      showTooltip: (args: { data: Datum; x: number; y: number }) => void;
      setTooltipData: (data: Datum | null) => void;
      reset: () => void;
      pin: () => void;
      unpin: () => void;
      anchorEl: PopperProps['anchorEl'];
      popperRef: PopperProps['popperRef'];
      tooltipRef: React.RefObject<HTMLDivElement | null>;
      tooltipData: Datum | null;
      tooltipOpen: boolean;
      isPinned: boolean;
      mode: TooltipMode;
    }
  | undefined;

const ChartTooltipContext = React.createContext<ChartTooltipContextType<any>>(undefined);
type PopperInstance = NonNullable<Extract<NonNullable<PopperProps['popperRef']>, React.RefObject<unknown>>['current']>;
type TooltipState<Datum> = {
  mode: TooltipMode;
  data: Datum | null;
};

export const ChartTooltipProvider = <Datum,>({ children, onClose }: React.PropsWithChildren<{ onClose?: () => void }>) => {
  const [state, setState] = React.useState<TooltipState<Datum>>({ data: null, mode: 'idle' });
  const positionRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const popperRef = React.useRef<PopperInstance>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const updatePosition = React.useCallback(
    rafThrottle((x: number, y: number) => {
      positionRef.current = { x, y };
      popperRef.current?.update();
    }),
    []
  );

  React.useEffect(() => () => updatePosition.clear(), [updatePosition]);

  const anchorEl = React.useMemo(
    () => ({
      getBoundingClientRect: () => {
        const x = positionRef.current?.x;
        const y = positionRef.current?.y;
        return new DOMRect(x, y, 0, 0);
      },
    }),
    []
  );

  const reset = React.useCallback(() => {
    setState({ data: null, mode: 'idle' });
    popperRef.current?.update();
    onClose?.();
  }, [onClose]);

  const hideTooltip = React.useCallback(() => {
    setState(p => {
      if (p.mode === 'pinned') return p;
      onClose?.();
      return { ...p, mode: 'idle' as const };
    });
    popperRef.current?.update();
  }, [onClose]);

  const showTooltip = React.useCallback(
    ({ data, x, y }: { data: Datum; x: number; y: number }) => {
      updatePosition(x, y);
      setState(p => ({ data, mode: p.mode === 'pinned' ? 'pinned' : 'hovering' }));
      popperRef.current?.update();
    },
    [updatePosition]
  );

  const setTooltipData = React.useCallback(
    (data: Datum | null) => {
      if (data) {
        setState(p => ({ data, mode: p.mode === 'pinned' ? 'pinned' : 'hovering' }));
      } else {
        setState(p => {
          if (p.mode === 'pinned') return p;
          onClose?.();
          return { data: null, mode: 'idle' as const };
        });
      }
      popperRef.current?.update();
    },
    [onClose]
  );

  const pin = React.useCallback(() => {
    setState(p => (p.data ? { ...p, mode: 'pinned' } : p));
    popperRef.current?.update();
  }, []);

  const unpin = React.useCallback(() => {
    setState(p => ({ ...p, mode: 'idle' }));
    popperRef.current?.update();
  }, []);

  const tooltipOpen = state.mode !== 'idle';
  const tooltipData = state.data;
  const isPinned = state.mode === 'pinned';
  const { mode } = state;

  const memoizedTooltipValue = React.useMemo(
    () => ({
      popperRef,
      anchorEl,
      tooltipData,
      tooltipOpen,
      isPinned,
      mode,
      updatePosition,
      hideTooltip,
      showTooltip,
      setTooltipData,
      reset,
      pin,
      unpin,
      tooltipRef,
    }),
    [
      popperRef,
      anchorEl,
      tooltipData,
      tooltipOpen,
      isPinned,
      mode,
      hideTooltip,
      showTooltip,
      setTooltipData,
      updatePosition,
      reset,
      pin,
      unpin,
      tooltipRef,
    ]
  );

  return <ChartTooltipContext.Provider value={memoizedTooltipValue}>{children}</ChartTooltipContext.Provider>;
};

export const useChartTooltipContext = <Datum,>() => {
  const ctx = useContext(ChartTooltipContext) as ChartTooltipContextType<Datum>;
  if (!ctx) throw new Error('useChartTooltipContext must be used within a ChartTooltipProvider');
  return ctx;
};
